import { Instagram, Facebook, Youtube } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { INSTAGRAM_URL, FACEBOOK_URL, YOUTUBE_URL } from "@/lib/cart";
import { asset } from "@/lib/siteAssets";
import { track, trackNavClick } from "@/lib/analytics";

const TILES = [
  asset("products/chicken.webp"),
  asset("products/avakai.webp"),
  asset("products/prawn.webp"),
  asset("products/mutton.webp"),
];

export const InstagramFeed = () => {
  const { t } = useLang();
  // Only the Instagram label has a real Telugu translation (this section
  // pre-dates the other two) — Facebook/YouTube are new, English-only for
  // now, same as other recently-added copy on this site.
  const followLinks = [
    // Instagram's icon points at the gradient <defs> rendered below — its
    // brand identity IS that gradient, a flat color wouldn't read as "the
    // real app". Facebook/YouTube are single-color brands, so a plain hex
    // value (passed straight through as the SVG stroke color) is enough.
    { id: "instagram", href: INSTAGRAM_URL, icon: Instagram, label: t.instagram.follow, color: "url(#instagram-gradient)" },
    { id: "facebook",  href: FACEBOOK_URL,  icon: Facebook,  label: "Follow on Facebook", color: "#1877F2" },
    { id: "youtube",   href: YOUTUBE_URL,   icon: Youtube,   label: "Follow on YouTube", color: "#FF0000" },
  ];
  return (
    <section
      data-testid="instagram-section"
      className="relative py-8 sm:py-10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-5">
          <div className="brand-ribbon mb-2">{t.instagram.ribbon}</div>
          <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-[#5A2D1D]">
            {t.instagram.h1}{" "}
            <span className="italic text-[#7A3B25]">{t.instagram.h2}</span>
          </h2>
          <p className="mt-1.5 text-sm text-[#2E1A12]/80 max-w-2xl mx-auto">
            {t.instagram.sub}
          </p>
        </div>

        <div
          data-testid="instagram-grid"
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
        >
          {TILES.map((src, i) => (
            <a
              key={i}
              data-testid={`instagram-tile-${i}`}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => track("Clicked on Instagram Feed Image", { tile_index: i, source: "instagram_section" })}
              className="group relative aspect-square overflow-hidden rounded-sm border border-[#5A2D1D]/20"
            >
              <img
                src={src}
                alt={`Instagram tile ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#5A2D1D]/0 group-hover:bg-[#5A2D1D]/60 transition-colors grid place-items-center">
                <Instagram className="w-8 h-8 text-[#F5E6CC] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ))}
        </div>

        {/* Zero-size, purely so the Instagram icon below can reference
            #instagram-gradient via its stroke color. */}
        <svg width="0" height="0" className="absolute" aria-hidden="true">
          <defs>
            <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFDC80" />
              <stop offset="25%" stopColor="#E1306C" />
              <stop offset="50%" stopColor="#C13584" />
              <stop offset="75%" stopColor="#833AB4" />
              <stop offset="100%" stopColor="#405DE6" />
            </linearGradient>
          </defs>
        </svg>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
          {followLinks.map(({ id, href, icon: Icon, label, color }) => (
            <a
              key={id}
              data-testid={`follow-button-${id}`}
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackNavClick(label, "instagram_section")}
              className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-7 py-3.5 rounded-sm font-semibold transition-colors"
            >
              <Icon className="w-4 h-4" color={color} />
              {label}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
